import React from 'react';
import { useLocation } from 'react-router-dom';
import { seoConfig } from '../utils/seoConfig';

const RichSnippets = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Generate dynamic rich snippets based on current page
  const generateRichSnippets = () => {
    const snippets = [];

    // Base Organization Rich Snippet (appears on all pages)
    const organizationSnippet = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${seoConfig.baseUrl}/#organization`,
      "name": "QuantumFrame Studio",
      "alternateName": [
        "Quantum Frame Studio",
        "QuantumFrame Studio Bhopal",
        "Quantum Frame Digital Agency"
      ],
      "description": "Leading web development and digital marketing agency in Bhopal, Madhya Pradesh. We specialize in custom website design, SEO optimization, and comprehensive digital solutions.",
      "url": seoConfig.baseUrl,
      "logo": `${seoConfig.baseUrl}/assets/images/quantumframe-logo.svg`,
      "image": [
        `${seoConfig.baseUrl}/assets/images/quantumframe-logo.svg`,
        `${seoConfig.baseUrl}/assets/images/office-team.jpg`,
        `${seoConfig.baseUrl}/assets/images/portfolio-showcase.jpg`
      ],
      "telephone": seoConfig.business.phone,
      "email": seoConfig.business.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "123 IT Park, Zone-II",
        "addressLocality": "Bhopal",
        "addressRegion": "Madhya Pradesh",
        "postalCode": "462022",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": seoConfig.location.coordinates.lat,
        "longitude": seoConfig.location.coordinates.lng
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "priceRange": "₹15,000 - ₹2,00,000",
      "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer", "UPI", "Online Payment"],
      "currenciesAccepted": "INR",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Rajesh Kumar"
          },
          "reviewBody": "Excellent web development services! QuantumFrame Studio created a beautiful, responsive website for our business. Highly professional team with great attention to detail.",
          "datePublished": "2024-01-15"
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Priya Sharma"
          },
          "reviewBody": "Outstanding SEO results! Our website traffic increased by 300% within 6 months. The team is knowledgeable and delivers on their promises.",
          "datePublished": "2024-02-03"
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "4",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Amit Patel"
          },
          "reviewBody": "Great digital marketing services. They helped us establish a strong social media presence and improved our online visibility significantly.",
          "datePublished": "2024-01-28"
        }
      ],
      "sameAs": [
        seoConfig.social.facebook,
        seoConfig.social.instagram,
        seoConfig.social.linkedin,
        seoConfig.social.twitter,
        seoConfig.social.youtube
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Digital Services Catalog",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Website Development",
            "description": "Custom responsive website development",
            "price": "25000",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "itemOffered": {
              "@type": "Service",
              "name": "Custom Website Development",
              "serviceType": "Web Development"
            }
          },
          {
            "@type": "Offer",
            "name": "SEO Services",
            "description": "Complete search engine optimization",
            "price": "15000",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "itemOffered": {
              "@type": "Service",
              "name": "SEO Optimization",
              "serviceType": "Digital Marketing"
            }
          },
          {
            "@type": "Offer",
            "name": "Social Media Marketing",
            "description": "Comprehensive social media management",
            "price": "12000",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "itemOffered": {
              "@type": "Service",
              "name": "Social Media Management",
              "serviceType": "Digital Marketing"
            }
          }
        ]
      }
    };

    snippets.push(organizationSnippet);

    // Page-specific rich snippets
    switch (currentPath) {
      case '/':
        // Homepage - Website Rich Snippet
        snippets.push({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "QuantumFrame Studio - Web Development & Digital Marketing",
          "url": seoConfig.baseUrl,
          "description": "Transform your digital presence with expert web development and marketing solutions in Bhopal, Madhya Pradesh.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${seoConfig.baseUrl}/search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          },
          "publisher": {
            "@id": `${seoConfig.baseUrl}/#organization`
          }
        });
        break;

      case '/services':
        // Services Page - Service Rich Snippet
        snippets.push({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "QuantumFrame Studio Services",
          "description": "Professional web development and digital marketing services",
          "numberOfItems": 6,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "Service",
                "name": "Website Development",
                "description": "Custom responsive websites using React, WordPress, and modern technologies",
                "provider": {
                  "@type": "Organization",
                  "name": "QuantumFrame Studio"
                },
                "areaServed": "Bhopal, Madhya Pradesh",
                "serviceOutput": "Professional Website",
                "offers": {
                  "@type": "Offer",
                  "price": "25000",
                  "priceCurrency": "INR",
                  "availability": "https://schema.org/InStock"
                }
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "Service",
                "name": "SEO Services",
                "description": "Complete search engine optimization for better rankings",
                "provider": {
                  "@type": "Organization",
                  "name": "QuantumFrame Studio"
                },
                "areaServed": "Bhopal, Madhya Pradesh",
                "serviceOutput": "Improved Search Rankings",
                "offers": {
                  "@type": "Offer",
                  "price": "15000",
                  "priceCurrency": "INR",
                  "availability": "https://schema.org/InStock"
                }
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "Service",
                "name": "Digital Marketing",
                "description": "Strategic digital marketing campaigns for business growth",
                "provider": {
                  "@type": "Organization",
                  "name": "QuantumFrame Studio"
                },
                "areaServed": "Bhopal, Madhya Pradesh",
                "serviceOutput": "Increased Online Presence",
                "offers": {
                  "@type": "Offer",
                  "price": "20000",
                  "priceCurrency": "INR",
                  "availability": "https://schema.org/InStock"
                }
              }
            }
          ]
        });
        break;

      case '/about-us':
        // About Page - Organization Details
        snippets.push({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About QuantumFrame Studio",
          "description": "Learn about our journey, team, and commitment to excellence in web development and digital marketing",
          "mainEntity": {
            "@type": "Organization",
            "name": "QuantumFrame Studio",
            "foundingDate": "2020",
            "founder": [
              {
                "@type": "Person",
                "name": "Aydin Khan",
                "jobTitle": "CEO & Founder"
              },
              {
                "@type": "Person",
                "name": "Maaz Ansari",
                "jobTitle": "Co-Founder"
              }
            ],
            "numberOfEmployees": {
              "@type": "QuantitativeValue",
              "minValue": 10,
              "maxValue": 25
            },
            "knowsAbout": [
              "Web Development",
              "Digital Marketing",
              "SEO Services",
              "Social Media Marketing",
              "E-commerce Development",
              "UI/UX Design"
            ]
          }
        });
        break;

      case '/testimonials':
        // Testimonials Page - Reviews Rich Snippet
        snippets.push({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "QuantumFrame Studio",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "127",
            "bestRating": "5",
            "worstRating": "1"
          },
          "review": [
            {
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": "5"
              },
              "author": {
                "@type": "Person",
                "name": "Sarah Johnson"
              },
              "reviewBody": "Exceptional service! QuantumFrame Studio transformed our online presence completely. The team is professional, creative, and delivers on time."
            },
            {
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": "5"
              },
              "author": {
                "@type": "Person",
                "name": "Michael Chen"
              },
              "reviewBody": "Outstanding results in SEO and digital marketing. Our website traffic increased by 250% in just 4 months. Highly recommend!"
            }
          ]
        });
        break;

      case '/contact':
        // Contact Page - Local Business
        snippets.push({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact QuantumFrame Studio",
          "description": "Get in touch with our team for web development and digital marketing services",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "QuantumFrame Studio",
            "telephone": seoConfig.business.phone,
            "email": seoConfig.business.email,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "123 IT Park, Zone-II",
              "addressLocality": "Bhopal",
              "addressRegion": "Madhya Pradesh",
              "postalCode": "462022",
              "addressCountry": "IN"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "09:00",
                "closes": "18:00"
              }
            ]
          }
        });
        break;

      case '/policy':
      case '/policies':
      case '/terms':
      case '/service-policies':
        // Policy Pages - Legal Document
        snippets.push({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "QuantumFrame Studio Service Policies",
          "description": "Comprehensive service policies and terms of service for all digital services",
          "datePublished": "2020-01-01",
          "dateModified": new Date().toISOString(),
          "author": {
            "@type": "Organization",
            "name": "QuantumFrame Studio"
          },
          "publisher": {
            "@type": "Organization",
            "name": "QuantumFrame Studio"
          },
          "mainEntity": {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are your payment terms?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We require 50% payment upfront and 50% upon project completion."
                }
              },
              {
                "@type": "Question",
                "name": "How many revisions do you provide?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We provide up to 3 rounds of revisions during the design phase."
                }
              }
            ]
          }
        });
        break;

      default:
        // Default case for other pages
        break;
    }

    // Add breadcrumb for all pages
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": seoConfig.baseUrl
      }
    ];

    if (currentPath !== '/') {
      const pageName = getPageName(currentPath);
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 2,
        "name": pageName,
        "item": `${seoConfig.baseUrl}${currentPath}`
      });
    }

    snippets.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    });

    return snippets;
  };

  // Helper function to get page name from path
  const getPageName = (path) => {
    const pageNames = {
      '/about-us': 'About Us',
      '/services': 'Services',
      '/contact': 'Contact Us',
      '/testimonials': 'Testimonials',
      '/policy': 'Service Policies',
      '/policies': 'Policies',
      '/terms': 'Terms & Conditions',
      '/service-policies': 'Service Policies'
    };
    return pageNames[path] || 'Page';
  };

  const richSnippets = generateRichSnippets();

  return (
    <>
      {richSnippets.map((snippet, index) => (
        <script
          key={`rich-snippet-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(snippet)
          }}
        />
      ))}
    </>
  );
};

export default RichSnippets;